import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { auth } from '../api/index';
import { admin } from '../api/admin';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSeo } from '../utils/seo';

const Login = () => {
  useSeo({
    title: 'Sign In',
    description:
      'Sign in to Nixook to rate shop carriers, print labels, track shipments and manage delivery notifications from one dashboard.',
    index: true,
  });

  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [systemLogo, setSystemLogo] = useState<string | null>(null);
  const [logoFailed, setLogoFailed] = useState(false);
  const { login, logout } = useAuth();
  const navigate = useNavigate();

  const getLogoUrl = (path: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.magento.local/v1/admin';
      const origin = new URL(baseUrl).origin;
      return `${origin}${path}`;
    } catch (e) {
      return path;
    }
  };

  useEffect(() => {
    logout();
    admin.getPublicSettings().then((data) => {
      if (data?.logo) setSystemLogo(data.logo);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        const res = await auth.login({ email, password });
        login(res);
        navigate('/');
      } else {
        const res = await auth.signup({ email, password, name });
        toast.success(res.message || 'Account created successfully!');
        setIsLogin(true);
      }
    } catch (err: any) {
      // Error is handled by global axios interceptor toast
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-xl">
        <div className="text-center mb-8">
          {systemLogo && !logoFailed && (
            <div className="flex justify-center mb-5">
              <img
                src={getLogoUrl(systemLogo)}
                alt="System Logo"
                className="h-12 max-w-[200px] object-contain"
                onError={() => setLogoFailed(true)}
              />
            </div>
          )}
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-slate-500">
            {isLogin ? 'Enter your credentials to continue' : 'Start monitoring your shipments today'}
          </p>
        </div>



        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Full Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
                placeholder="John Doe"
                required
                disabled={isLoading}
              />
            </div>
          )}
          
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Email Address <span className="text-red-500">*</span></label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
              placeholder="name@company.com"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Password <span className="text-red-500">*</span></label>
              {isLogin && (
                <a
                  href="/forgot-password"
                  className={`text-xs text-indigo-600 hover:text-indigo-700 font-medium transition-colors ${isLoading ? 'pointer-events-none opacity-50' : ''}`}
                >
                  Forgot password?
                </a>
              )}
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 pr-11 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
                placeholder="••••••••"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                disabled={isLoading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg transition-all shadow-lg shadow-indigo-600/20 flex items-center justify-center disabled:bg-indigo-400 disabled:cursor-not-allowed disabled:shadow-none"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isLogin ? 'Signing In...' : 'Creating Account...'}
              </>
            ) : (
              isLogin ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-slate-500">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          {isLogin ? (
            <a
              href={import.meta.env.VITE_FRONTEND_URL || 'https://shipweb.magento.local'}
              className="text-indigo-600 hover:text-indigo-700 font-medium transition-colors cursor-pointer"
            >
              Sign up
            </a>
          ) : (
            <button
              type="button"
              onClick={() => !isLoading && setIsLogin(!isLogin)}
              disabled={isLoading}
              className="text-indigo-600 hover:text-indigo-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Log in
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
